import React from "react";

export function getBracketNodePositions(bracket, rounds) {
  // Note: rounds parameter is kept for backward compatibility but actual rounds are now calculated from bracket data
  // Responsive dimensions based on screen size - updated for compact design
  // Better mobile detection that accounts for landscape orientation
  const isMobile = typeof window !== 'undefined' && (
    window.innerWidth <= 768 ||
    (window.innerWidth <= 1024 && window.innerHeight <= 768) || // Landscape mobile/tablet
    ('ontouchstart' in window && Math.min(window.innerWidth, window.innerHeight) <= 768) // Touch device check
  );

  // Check if in landscape mode (width > height)
  const isLandscape = typeof window !== 'undefined' && window.innerWidth > window.innerHeight;

  const boxW = 160; // Compact width for both mobile and desktop
  const boxH = 48; // Compact height to match new two-row MatchBox design
  const roundGap = isMobile ? 80 : 100; // Reduced by half to save space
  const yBase = 0, xBase = isMobile ? 10 : 20; // Left padding only (mobile: 10px, desktop: 20px)
  // Increase vertical gap significantly in landscape mode to prevent overlap
  const minMatchGap = isMobile ? (isLandscape ? 50 : 25) : 30;

  const pos = {};

  // Check tournament type
  const isSingleElimination = bracket.some(m => m.bracket === "SE");
  const isModifiedSingleElimination = bracket.some(m => m.bracket === "MSE");

  if (isModifiedSingleElimination) {
    // Modified Single Elimination positioning - APA format
    // Winners bracket flows RIGHT, Flip bracket is on the LEFT, Finals in CENTER
    const mseMatches = bracket.filter(m => m.bracket === "MSE");

    // Determine bracket size based on the maximum column number in the template
    // This is more reliable than counting col 1 matches since some may be BYE-advanced
    // 8-player template has max col 8, 16-player template has max col 9, 32-player template has max col 12
    const maxCol = Math.max(...mseMatches.map(m => m.col));
    const bracketSize = maxCol <= 8 ? 8 : (maxCol <= 9 ? 16 : 32);

    const mseGap = 40; // Gap between columns

    if (bracketSize === 8) {
      // 8-player APA MSE layout - matches the template in modifiedSingleEliminationUtils.js
      // Template has 14 matches across columns 1-8:
      // Winners: Col 1 (M1-M4), Col 2 (M5-M6), Col 3 (M7)
      // Flip: Col 4 (M8-M9), Col 5 (M10-M11), Col 6 (M12), Col 7 (M13)
      // Final: Col 8 (M14)

      // APA Layout: Initial matches CENTER, Winners go RIGHT, Losers drop LEFT with CROSSING
      // When losers drop to flip bracket, they cross over (top goes to bottom, bottom goes to top)
      const verticalSpacing = boxH + minMatchGap;

      mseMatches.forEach(match => {
        let xPos, yPos;

        // Winners bracket - Initial matches in CENTER, winners flow RIGHT
        if (match.col === 1) {
          // Column 1: 4 initial matches (rows 0-3) - CENTER position (x=4)
          xPos = 4;
          yPos = match.row * verticalSpacing;
        } else if (match.col === 2) {
          // Column 2: M5-M6 Winners round 2 - RIGHT of initial (x=5)
          xPos = 5;
          yPos = (match.row * 2 + 0.5) * verticalSpacing;
        } else if (match.col === 3) {
          // Column 3: M7 Winners semi-final - further RIGHT (x=6)
          xPos = 6;
          yPos = 1.5 * verticalSpacing;
        }
        // Flip bracket - Losers drop LEFT
        // First flip column (col 4) stays in same vertical order
        // Subsequent columns reverse to create crossing pattern
        else if (match.col === 4) {
          // Column 4: M8-M9 First flip round - LEFT of initial (x=3)
          // NO REVERSE - same vertical alignment as source matches
          xPos = 3;
          yPos = (match.row * 2 + 0.5) * verticalSpacing;
        } else if (match.col === 5) {
          // Column 5: M10-M11 Flip round 2 - further LEFT (x=2)
          // REVERSE: row 0->1, row 1->0
          xPos = 2;
          const reversedRow = 1 - match.row;
          yPos = (reversedRow * 2 + 0.5) * verticalSpacing;
        } else if (match.col === 6) {
          // Column 6: M12 Flip semi-final - further LEFT (x=1)
          xPos = 1;
          yPos = 1.5 * verticalSpacing;
        }
        // Convergence and Final
        else if (match.col === 7) {
          // Column 7: M13 Convergence (Loser M7 vs Winner M12) - LEFT side (x=0)
          xPos = 0;
          yPos = 1.5 * verticalSpacing;
        } else if (match.col === 8) {
          // Column 8: M14 Championship final - FAR RIGHT (x=7)
          xPos = 7;
          yPos = 1.5 * verticalSpacing;
        } else {
          // Fallback
          xPos = match.col - 1;
          yPos = match.row * verticalSpacing;
        }

        pos[match.id] = {
          x: xBase + xPos * (boxW + mseGap),
          y: yBase + 60 + yPos,
          w: boxW,
          h: boxH
        };
      });
    } else if (bracketSize === 16) {
      // 16-player APA MSE layout - matches the template in modifiedSingleEliminationUtils.js
      // Template has 29 matches across columns 1-9:
      // Winners: Col 1 (M1-M8), Col 2 (M9-M12), Col 3 (M13-M14) - NO M15, winners wait for convergence
      // Flip: Col 4 (M15-M18), Col 5 (M19-M22), Col 6 (M23-M24), Col 7 (M25-M26)
      // Convergence: Col 8 (M27-M28) - Flip winners return to play winners bracket
      // Final: Col 9 (M29)

      // APA Layout: Initial matches CENTER, Winners go RIGHT, Losers drop LEFT with CROSSING
      // When losers drop to flip bracket, they cross over (top goes to bottom, bottom goes to top)
      // This creates the characteristic X pattern in MSE brackets

      const verticalSpacing = boxH + minMatchGap;

      mseMatches.forEach(match => {
        let xPos, yPos;

        // Winners bracket - Initial matches in CENTER, winners flow RIGHT
        if (match.col === 1) {
          // Column 1: M1-M8 Initial matches - CENTER position (x=5)
          xPos = 5;
          yPos = match.row * verticalSpacing;
        } else if (match.col === 2) {
          // Column 2: M9-M12 Winners round 2 - RIGHT of initial (x=6)
          xPos = 6;
          yPos = (match.row * 2 + 0.5) * verticalSpacing;
        } else if (match.col === 3) {
          // Column 3: M13-M14 Winners round 3 - further RIGHT (x=7)
          // These winners WAIT for convergence matches (no M15)
          xPos = 7;
          yPos = (match.row * 4 + 1.5) * verticalSpacing;
        }
        // Flip bracket - Losers drop LEFT
        // First flip column (col 4) stays in same vertical order
        // Subsequent columns reverse to create crossing pattern
        else if (match.col === 4) {
          // Column 4: M15-M18 First flip round - LEFT of initial (x=4)
          // NO REVERSE - same vertical alignment as source matches
          xPos = 4;
          yPos = (match.row * 2 + 0.5) * verticalSpacing;
        } else if (match.col === 5) {
          // Column 5: M19-M22 Flip round 2 - further LEFT (x=3)
          // REVERSE: row 0->3, row 1->2, row 2->1, row 3->0
          xPos = 3;
          const reversedRow = 3 - match.row;
          yPos = (reversedRow * 2 + 0.5) * verticalSpacing;
        } else if (match.col === 6) {
          // Column 6: M23-M24 Flip round 3 - further LEFT (x=2)
          // NO REVERSE for this column
          xPos = 2;
          yPos = (match.row * 4 + 1.5) * verticalSpacing;
        } else if (match.col === 7) {
          // Column 7: M25-M26 Flip round 4 - further LEFT (x=1)
          // REVERSE: row 0->1, row 1->0
          xPos = 1;
          const reversedRow = 1 - match.row;
          yPos = (reversedRow * 4 + 1.5) * verticalSpacing;
        }
        // Convergence matches - Flip winners return to RIGHT side to play Winners bracket
        else if (match.col === 8) {
          // Column 8: M27-M28 Convergence matches - RIGHT of winners bracket (x=8)
          // M27: Winner of M13 vs Winner of M25
          // M28: Winner of M14 vs Winner of M26
          xPos = 8;
          yPos = (match.row * 4 + 1.5) * verticalSpacing;
        }
        // Championship Final
        else if (match.col === 9) {
          // Column 9: M29 Championship final - FAR RIGHT (x=9)
          xPos = 9;
          yPos = 3.5 * verticalSpacing;
        } else {
          // Fallback
          xPos = match.col - 1;
          yPos = match.row * verticalSpacing;
        }

        pos[match.id] = {
          x: xBase + xPos * (boxW + mseGap),
          y: yBase + 60 + yPos,
          w: boxW,
          h: boxH
        };
      });
    } else if (bracketSize === 32) {
      // 32-player APA MSE layout - matches the template in modifiedSingleEliminationUtils.js
      // Template has 61 matches across columns 1-12:
      // Winners: Col 1 (M1-M16), Col 2 (M17-M24), Col 3 (M25-M28), Col 4 (M29-M30) - winners wait for convergence
      // Flip: Col 5 (M31-M38), Col 6 (M39-M46), Col 7 (M47-M50), Col 8 (M51-M54), Col 9 (M55-M56), Col 10 (M57-M58)
      // Convergence: Col 11 (M59-M60) - Flip winners return to play winners bracket
      // Final: Col 12 (M61)

      const verticalSpacing = boxH + minMatchGap;

      mseMatches.forEach(match => {
        let xPos, yPos;

        // Winners bracket - Initial matches in CENTER, winners flow RIGHT
        if (match.col === 1) {
          // Column 1: M1-M16 Initial matches - CENTER position (x=7)
          xPos = 7;
          yPos = match.row * verticalSpacing;
        } else if (match.col === 2) {
          // Column 2: M17-M24 Winners round 2 - RIGHT of initial (x=8)
          xPos = 8;
          yPos = (match.row * 2 + 0.5) * verticalSpacing;
        } else if (match.col === 3) {
          // Column 3: M25-M28 Winners round 3 - further RIGHT (x=9)
          xPos = 9;
          yPos = (match.row * 4 + 1.5) * verticalSpacing;
        } else if (match.col === 4) {
          // Column 4: M29-M30 Winners round 4 - further RIGHT (x=10)
          // These winners WAIT for convergence matches
          xPos = 10;
          yPos = (match.row * 8 + 3.5) * verticalSpacing;
        }
        // Flip bracket - Losers drop LEFT
        else if (match.col === 5) {
          // Column 5: M31-M38 First flip round - LEFT of initial (x=6)
          // NO REVERSE - same vertical alignment as source matches
          xPos = 6;
          yPos = (match.row * 2 + 0.5) * verticalSpacing;
        } else if (match.col === 6) {
          // Column 6: M39-M46 Flip round 2 - further LEFT (x=5)
          // REVERSE: row 0->7, row 1->6, etc.
          xPos = 5;
          const reversedRow = 7 - match.row;
          yPos = (reversedRow * 2 + 0.5) * verticalSpacing;
        } else if (match.col === 7) {
          // Column 7: M47-M50 Flip round 3 - further LEFT (x=4)
          // NO REVERSE
          xPos = 4;
          yPos = (match.row * 4 + 1.5) * verticalSpacing;
        } else if (match.col === 8) {
          // Column 8: M51-M54 Flip round 4 - further LEFT (x=3)
          // REVERSE: row 0->3, row 1->2, etc.
          xPos = 3;
          const reversedRow = 3 - match.row;
          yPos = (reversedRow * 4 + 1.5) * verticalSpacing;
        } else if (match.col === 9) {
          // Column 9: M55-M56 Flip round 5 - further LEFT (x=2)
          // NO REVERSE
          xPos = 2;
          yPos = (match.row * 8 + 3.5) * verticalSpacing;
        } else if (match.col === 10) {
          // Column 10: M57-M58 Flip round 6 - further LEFT (x=1)
          // REVERSE: row 0->1, row 1->0
          xPos = 1;
          const reversedRow = 1 - match.row;
          yPos = (reversedRow * 8 + 3.5) * verticalSpacing;
        }
        // Convergence matches - Flip winners return to RIGHT side to play Winners bracket
        else if (match.col === 11) {
          // Column 11: M59-M60 Convergence matches - RIGHT of winners bracket (x=11)
          xPos = 11;
          yPos = (match.row * 8 + 3.5) * verticalSpacing;
        }
        // Championship Final
        else if (match.col === 12) {
          // Column 12: M61 Championship final - FAR RIGHT (x=12)
          xPos = 12;
          yPos = 7.5 * verticalSpacing;
        } else {
          // Fallback
          xPos = match.col - 1;
          yPos = match.row * verticalSpacing;
        }

        pos[match.id] = {
          x: xBase + xPos * (boxW + mseGap),
          y: yBase + 60 + yPos,
          w: boxW,
          h: boxH
        };
      });
    } else {
      // Fallback for other sizes - simple column layout
      const matchesByColumn = {};
      mseMatches.forEach(match => {
        if (!matchesByColumn[match.col]) {
          matchesByColumn[match.col] = [];
        }
        matchesByColumn[match.col].push(match);
      });

      const columns = Object.keys(matchesByColumn).map(Number).sort((a, b) => a - b);

      columns.forEach(col => {
        const colMatches = matchesByColumn[col];
        colMatches.sort((a, b) => a.row - b.row);

        colMatches.forEach((match, idx) => {
          pos[match.id] = {
            x: xBase + (col - 1) * (boxW + roundGap),
            y: yBase + 60 + idx * (boxH + minMatchGap),
            w: boxW,
            h: boxH,
          };
        });
      });
    }

    return pos;
  } else if (isSingleElimination) {
    // Single Elimination positioning - use actual max round numbers instead of calculated rounds
    const singleElimMatches = bracket.filter(m => m.bracket === "SE");
    const maxSingleElimRound = singleElimMatches.length > 0 ? Math.max(...singleElimMatches.map(m => m.round)) : 1;
    const matchesByRound = [];

    for (let r = 1; r <= maxSingleElimRound; r++) {
      matchesByRound.push(singleElimMatches.filter(m => m.round === r));
    }

    const startY = yBase + 60;

    // Find the maximum number of matches in any round to determine bracket height
    const maxMatchesInAnyRound = Math.max(...matchesByRound.map(r => r.length), 1);
    const maxRoundHeight = Math.max(maxMatchesInAnyRound * (boxH + minMatchGap) - minMatchGap, boxH);

    // For backward compatibility, keep firstRoundHeight reference
    const firstRoundHeight = maxRoundHeight;

    // Position single elimination matches
    for (let roundIdx = 0; roundIdx < matchesByRound.length; roundIdx++) {
      const roundMatches = matchesByRound[roundIdx];
      if (roundMatches.length === 0) continue;

      // All rounds - distribute evenly across the max round height
      if (roundMatches.length === 1) {
        // Single match - center it
        pos[roundMatches[0].id] = {
          x: xBase + roundIdx * (boxW + roundGap),
          y: startY + (firstRoundHeight / 2) - (boxH / 2),
          w: boxW,
          h: boxH,
        };
      } else {
        // Multiple matches - distribute evenly
        const availableHeight = firstRoundHeight;
        const spacing = availableHeight / (roundMatches.length + 1);

        // Ensure minimum spacing between matches
        const minSpaceBetweenMatches = boxH + minMatchGap;
        const actualSpacing = Math.max(spacing, minSpaceBetweenMatches);

        for (let matchIdx = 0; matchIdx < roundMatches.length; matchIdx++) {
          const match = roundMatches[matchIdx];
          // Use actualSpacing for positioning, but center the group within availableHeight if using min spacing
          let yPosition;
          if (actualSpacing > spacing) {
            // Using minimum spacing - center the entire group
            const totalGroupHeight = (roundMatches.length - 1) * actualSpacing;
            const groupStartY = Math.max(startY, startY + (firstRoundHeight - totalGroupHeight) / 2);
            yPosition = groupStartY + matchIdx * actualSpacing;
          } else {
            // Using calculated spacing - distribute evenly
            yPosition = startY + spacing * (matchIdx + 1) - (boxH / 2);
          }

          pos[match.id] = {
            x: xBase + roundIdx * (boxW + roundGap),
            y: yPosition,
            w: boxW,
            h: boxH,
          };
        }
      }
    }

    return pos;
  }

  // Double Elimination positioning (existing code)
  // Group matches by bracket and round
  const winners = bracket.filter(m => m.bracket === "W");
  const losers = bracket.filter(m => m.bracket === "L");
  const grandFinals = bracket.filter(m => m.bracket === "GF");

  // Winners bracket positioning - use actual max round numbers instead of calculated rounds
  const maxWinnersRound = winners.length > 0 ? Math.max(...winners.map(m => m.round)) : 1;
  const winnersByRound = [];
  for (let r = 1; r <= maxWinnersRound; r++) {
    winnersByRound.push(winners.filter(m => m.round === r));
  }

  // Calculate winners bracket height based on the tallest round
  const winnersStartY = yBase + 60; // Increased from 30 to 60 to leave room for headers

  // Find the maximum number of matches in any winners round to determine bracket height
  const maxMatchesInAnyRound = Math.max(...winnersByRound.map(r => r.length), 1);
  const maxRoundHeight = Math.max(maxMatchesInAnyRound * (boxH + minMatchGap) - minMatchGap, boxH);

  // For backward compatibility, keep firstRoundHeight reference
  const firstRoundHeight = maxRoundHeight;

  // Position winners bracket matches
  for (let roundIdx = 0; roundIdx < winnersByRound.length; roundIdx++) {
    const roundMatches = winnersByRound[roundIdx];
    if (roundMatches.length === 0) continue;

    // All rounds - distribute evenly across the max round height
    if (roundMatches.length === 1) {
      // Single match - center it
      pos[roundMatches[0].id] = {
        x: xBase + roundIdx * (boxW + roundGap),
        y: winnersStartY + (firstRoundHeight / 2) - (boxH / 2),
        w: boxW,
        h: boxH,
      };
    } else {
      // Multiple matches - distribute evenly across available height
      const availableHeight = firstRoundHeight;
      const spacing = availableHeight / (roundMatches.length + 1);

      // Ensure minimum spacing between matches
      const minSpaceBetweenMatches = boxH + minMatchGap;
      const actualSpacing = Math.max(spacing, minSpaceBetweenMatches);

      for (let matchIdx = 0; matchIdx < roundMatches.length; matchIdx++) {
        const match = roundMatches[matchIdx];
        // Use actualSpacing for positioning, but center the group within availableHeight if using min spacing
        let yPosition;
        if (actualSpacing > spacing) {
          // Using minimum spacing - center the entire group
          const totalGroupHeight = (roundMatches.length - 1) * actualSpacing;
          const groupStartY = Math.max(winnersStartY, winnersStartY + (firstRoundHeight - totalGroupHeight) / 2);
          yPosition = groupStartY + matchIdx * actualSpacing;
        } else {
          // Using calculated spacing - distribute evenly
          yPosition = winnersStartY + spacing * (matchIdx + 1) - (boxH / 2);
        }

        pos[match.id] = {
          x: xBase + roundIdx * (boxW + roundGap),
          y: yPosition,
          w: boxW,
          h: boxH,
        };
      }
    }
  }

  // Calculate the actual height used by winners bracket
  const winnersPositions = Object.keys(pos)
    .filter(key => bracket.find(m => m.id === key)?.bracket === "W")
    .map(key => pos[key]);

  const winnersMinY = winnersPositions.length > 0 ? Math.min(...winnersPositions.map(p => p.y)) : winnersStartY;
  const winnersMaxY = winnersPositions.length > 0 ? Math.max(...winnersPositions.map(p => p.y + p.h)) : winnersStartY + boxH;

  // Losers bracket positioning
  const losersByRound = [];
  const loserRounds = [...new Set(losers.map(m => m.round))].sort((a, b) => a - b);

  for (let round of loserRounds) {
    losersByRound.push(losers.filter(m => m.round === round));
  }

  // Position losers bracket with consistent spacing and adequate separation from winners
  const losersStartY = winnersMaxY + 80; // Increased gap from 60 to 80 pixels between winners and losers

  // Calculate the total height for the losers bracket based on the tallest round
  const maxLosersMatchesInAnyRound = Math.max(...losersByRound.map(r => r.length), 1);
  const maxLosersRoundHeight = Math.max(maxLosersMatchesInAnyRound * (boxH + minMatchGap) - minMatchGap, boxH);

  // For backward compatibility, keep firstLosersRoundHeight reference
  const firstLosersRoundHeight = maxLosersRoundHeight;

  for (let roundIdx = 0; roundIdx < losersByRound.length; roundIdx++) {
    const roundMatches = losersByRound[roundIdx];
    if (roundMatches.length === 0) continue;

    // All rounds - distribute evenly across the max round height
    if (roundMatches.length === 1) {
      // Single match - center it
      pos[roundMatches[0].id] = {
        x: xBase + roundIdx * (boxW + roundGap),
        y: losersStartY + (firstLosersRoundHeight / 2) - (boxH / 2),
        w: boxW,
        h: boxH,
      };
    } else {
      // Multiple matches - distribute evenly across available height
      const availableHeight = firstLosersRoundHeight;
      const spacing = availableHeight / (roundMatches.length + 1);

      // Ensure minimum spacing between matches
      const minSpaceBetweenMatches = boxH + minMatchGap;
      const actualSpacing = Math.max(spacing, minSpaceBetweenMatches);

      for (let matchIdx = 0; matchIdx < roundMatches.length; matchIdx++) {
        const match = roundMatches[matchIdx];
        // Use actualSpacing for positioning, but center the group within availableHeight if using min spacing
        let yPosition;
        if (actualSpacing > spacing) {
          // Using minimum spacing - center the entire group
          const totalGroupHeight = (roundMatches.length - 1) * actualSpacing;
          const groupStartY = Math.max(losersStartY, losersStartY + (firstLosersRoundHeight - totalGroupHeight) / 2);
          yPosition = groupStartY + matchIdx * actualSpacing;
        } else {
          // Using calculated spacing - distribute evenly
          yPosition = losersStartY + spacing * (matchIdx + 1) - (boxH / 2);
        }

        pos[match.id] = {
          x: xBase + roundIdx * (boxW + roundGap),
          y: yPosition,
          w: boxW,
          h: boxH,
        };
      }
    }
  }

  // Grand Finals positioning - one column to the right of Winners Final
  if (grandFinals.length > 0) {
    // Find the actual Winners Final match to position relative to it
    const winnersFinalMatch = winnersByRound[winnersByRound.length - 1]?.filter(m => m !== null)[0];

    let grandFinalX, grandFinalY;
    if (winnersFinalMatch && pos[winnersFinalMatch.id]) {
      // Position one column to the right of Winners Final
      grandFinalX = pos[winnersFinalMatch.id].x + (boxW + roundGap);
      grandFinalY = pos[winnersFinalMatch.id].y;
    } else {
      // Fallback calculation - one column past Winners Final
      grandFinalX = xBase + winnersByRound.length * (boxW + roundGap);
      grandFinalY = (winnersMinY + winnersMaxY) / 2 - boxH / 2;
    }

    for (let i = 0; i < grandFinals.length; i++) {
      const match = grandFinals[i];
      pos[match.id] = {
        x: grandFinalX + i * (boxW + roundGap), // Place GF Reset to the right of GF
        y: grandFinalY,
        w: boxW,
        h: boxH,
      };
    }
  }

  return pos;
}

export function isTournamentComplete(bracket) {
  // Check tournament type
  const isModifiedSingleElimination = bracket.some(m => m.bracket === "MSE");
  const isSingleElimination = bracket.some(m => m.bracket === "SE");
  
  if (isModifiedSingleElimination) {
    // Find the final match (highest column number)
    const maxCol = Math.max(...bracket.map(m => m.col));
    const finalMatch = bracket.find(m => m.bracket === "MSE" && m.col === maxCol);
    return finalMatch && finalMatch.finished;
  } else if (isSingleElimination) {
    // Find the final match (highest round number)
    const maxRound = Math.max(...bracket.map(m => m.round));
    const finalMatch = bracket.find(m => m.bracket === "SE" && m.round === maxRound);
    return finalMatch && finalMatch.finished;
  }
  
  // Double elimination logic
  // Tournament is complete if:
  // 1. Grand Final Reset is finished, OR
  // 2. Grand Final is finished AND the winner is the winners bracket player (p1)
  //    (meaning no reset is needed because the undefeated player won)
  const gf = bracket.find(m => m.bracket === "GF" && !m.id.includes("reset"));
  const gfReset = bracket.find(m => m.id === "gf-1-reset");

  // If Grand Final Reset is finished, tournament is complete
  if (gfReset && gfReset.finished) {
    return true;
  }

  // If Grand Final is finished, check if winners bracket player won
  if (gf && gf.finished) {
    const gfWinner = gf.winner;
    const winnersPlayerWon = gf.p1?.name === gfWinner &&
      !gfWinner?.includes('Winner of') &&
      !gfWinner?.includes('Loser of');

    // If losers bracket player (p2) won, reset is needed - tournament not complete yet
    // If winners bracket player (p1) won, tournament is complete
    return winnersPlayerWon;
  }

  return false;
}

export function ConnectLine({ from, to, nodes }) {
  const a = nodes[from];
  const b = nodes[to];
  if (!a || !b) return null;

  // Determine direction of connection
  const isLeftToRight = a.x < b.x;

  if (isLeftToRight) {
    // Standard left-to-right connection
    const ax = a.x + a.w;
    const ay = a.y + a.h / 2;
    const bx = b.x;
    const by = b.y + b.h / 2;

    // Calculate control points for smooth bezier curve
    const horizontalDist = bx - ax;
    const controlOffset = horizontalDist * 0.5; // Control point distance

    // Create smooth S-curve using cubic bezier
    // M = move to start, C = cubic bezier (control1, control2, end)
    const pathD = `M ${ax},${ay} C ${ax + controlOffset},${ay} ${bx - controlOffset},${by} ${bx},${by}`;

    return (
      <path
        d={pathD}
        fill="none"
        stroke="#999"
        strokeWidth={2}
      />
    );
  } else {
    // Right-to-left connection (for left branching)
    const ax = a.x;
    const ay = a.y + a.h / 2;
    const bx = b.x + b.w;
    const by = b.y + b.h / 2;

    // Calculate control points for smooth bezier curve
    const horizontalDist = ax - bx;
    const controlOffset = horizontalDist * 0.5;

    // Create smooth S-curve using cubic bezier
    const pathD = `M ${ax},${ay} C ${ax - controlOffset},${ay} ${bx + controlOffset},${by} ${bx},${by}`;

    return (
      <path
        d={pathD}
        fill="none"
        stroke="#999"
        strokeWidth={2}
      />
    );
  }
}